import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import logger from '@/lib/logger';

const ThemeContext = createContext(null);

function hexToHSL(hex) {
  let r = parseInt(hex.slice(1, 3), 16) / 255;
  let g = parseInt(hex.slice(3, 5), 16) / 255;
  let b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: break;
    }
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function applyThemeToDOM(darkMode, primaryColor, accentColor) {
  const root = document.documentElement;
  if (darkMode) {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
  root.style.setProperty('--theme-primary', primaryColor);
  root.style.setProperty('--theme-accent', accentColor);
  root.style.setProperty('--theme-primary-hsl', hexToHSL(primaryColor));
  root.style.setProperty('--theme-accent-hsl', hexToHSL(accentColor));
  root.style.setProperty('--ring', hexToHSL(primaryColor));
  root.style.setProperty('--chart-1', hexToHSL(primaryColor));
}

const DEFAULTS = { dark_mode: false, primary_color: '#0047AB', accent_color: '#059669' };

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(DEFAULTS);
  const [loaded, setLoaded] = useState(false);

  const loadTheme = useCallback(async () => {
    try {
      const { data } = await api.getTheme();
      setTheme(data);
      applyThemeToDOM(data.dark_mode, data.primary_color, data.accent_color);
    } catch (err) {
      logger.error(err);
      applyThemeToDOM(DEFAULTS.dark_mode, DEFAULTS.primary_color, DEFAULTS.accent_color);
    }
    setLoaded(true);
  }, []);

  const saveTheme = useCallback(async (newTheme) => {
    setTheme(newTheme);
    applyThemeToDOM(newTheme.dark_mode, newTheme.primary_color, newTheme.accent_color);
    try {
      await api.saveTheme(newTheme);
    } catch (err) {
      logger.error(err);
    }
  }, []);

  useEffect(() => {
    loadTheme();
  }, [loadTheme]);

  const themeValue = useMemo(() => ({ theme, saveTheme, loaded, loadTheme }), [theme, saveTheme, loaded, loadTheme]);

  return (
    <ThemeContext.Provider value={themeValue}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be inside ThemeProvider');
  return ctx;
}
